import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import {
  buscarConfiguracaoEmpresa,
  salvarConfiguracaoEmpresa,
  buscarConfiguracaoAcoes,
  salvarConfiguracaoAcao
} from '../../../services/configuracaoFiscal'
import {
  ConfiguracaoEmpresa,
  ConfiguracaoFiscalAcao,
  TIPO_ACAO_FISCAL
} from '../../../interfaces/configuracaoFiscal'

const CRT_OPCOES = [
  { valor: 1, label: '1 - Simples Nacional' },
  { valor: 2, label: '2 - Simples Nacional - excesso de sublimite' },
  { valor: 3, label: '3 - Regime Normal' },
  { valor: 4, label: '4 - Simples Nacional (MEI)' }
]

const campoSx = { margin: '0.6vh 0', width: '100%' }

const ConfiguracaoFiscalTab: React.FC = () => {
  const [empresa, setEmpresa] = useState<ConfiguracaoEmpresa | null>(null)
  const [acoes, setAcoes] = useState<ConfiguracaoFiscalAcao[]>([])
  const [acaoIndex, setAcaoIndex] = useState<number>(0)

  useEffect(() => {
    carregar()
  }, [])

  const carregar = async () => {
    try {
      const [empresaCarregada, acoesCarregadas] = await Promise.all([
        buscarConfiguracaoEmpresa(),
        buscarConfiguracaoAcoes()
      ])
      setEmpresa(empresaCarregada)
      setAcoes(acoesCarregadas)
    } catch {
      // withAxios já exibe o feedback de erro
    }
  }

  const atualizarEmpresa = (campo: keyof ConfiguracaoEmpresa, valor: any) => {
    if (!empresa) return
    setEmpresa({ ...empresa, [campo]: valor })
  }

  const handleSalvarEmpresa = async () => {
    if (!empresa) return
    const atualizada = await salvarConfiguracaoEmpresa(empresa)
    setEmpresa(atualizada)
  }

  const acaoAtual = acoes[acaoIndex]

  // Autorizar (venda) tem regras de CFOP diferentes das devolucoes.
  const ehAutorizar = acaoAtual?.tipoAcao === 1

  const numeroOuNulo = (valor: string) => (valor === '' ? null : Number(valor))

  const atualizarAcao = (campo: keyof ConfiguracaoFiscalAcao, valor: any) => {
    setAcoes(acoes.map((a, i) => (i === acaoIndex ? { ...a, [campo]: valor } : a)))
  }

  const atualizarCfop = (cfopIndex: number, campo: 'cfopOrigem' | 'cfopDestino', valor: number) => {
    const conversoes = acaoAtual.conversoesCfop.map((c, i) =>
      i === cfopIndex ? { ...c, [campo]: valor } : c
    )
    atualizarAcao('conversoesCfop', conversoes)
  }

  const adicionarCfop = () => {
    atualizarAcao('conversoesCfop', [
      ...acaoAtual.conversoesCfop,
      { id: '00000000-0000-0000-0000-000000000000', cfopOrigem: 0, cfopDestino: 0 }
    ])
  }

  const removerCfop = (cfopIndex: number) => {
    atualizarAcao(
      'conversoesCfop',
      acaoAtual.conversoesCfop.filter((_, i) => i !== cfopIndex)
    )
  }

  const handleSalvarAcao = async () => {
    if (!acaoAtual) return
    const atualizada = await salvarConfiguracaoAcao(acaoAtual)
    setAcoes(acoes.map((a, i) => (i === acaoIndex ? atualizada : a)))
  }

  if (!empresa) {
    return <Typography sx={{ p: 2 }}>Carregando configuração fiscal…</Typography>
  }

  return (
    <Box sx={{ p: 1, maxHeight: '70vh', overflowY: 'auto', width: '100%' }}>
      <Typography variant="h6" component="h1" gutterBottom>
        Emitente
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <TextField sx={campoSx} size="small" label="CNPJ" value={empresa.cnpj ?? ''} onChange={e => atualizarEmpresa('cnpj', e.target.value)} />
        <TextField sx={campoSx} size="small" label="Inscrição Estadual" value={empresa.inscricaoEstadual ?? ''} onChange={e => atualizarEmpresa('inscricaoEstadual', e.target.value)} />
        <TextField sx={campoSx} size="small" label="Razão Social" value={empresa.razaoSocial ?? ''} onChange={e => atualizarEmpresa('razaoSocial', e.target.value)} />
        <TextField sx={campoSx} size="small" label="Nome Fantasia" value={empresa.nomeFantasia ?? ''} onChange={e => atualizarEmpresa('nomeFantasia', e.target.value)} />
        <TextField sx={campoSx} size="small" label="Logradouro" value={empresa.logradouro ?? ''} onChange={e => atualizarEmpresa('logradouro', e.target.value)} />
        <TextField sx={campoSx} size="small" label="Número" value={empresa.numero ?? ''} onChange={e => atualizarEmpresa('numero', e.target.value)} />
        <TextField sx={campoSx} size="small" label="Complemento" value={empresa.complemento ?? ''} onChange={e => atualizarEmpresa('complemento', e.target.value)} />
        <TextField sx={campoSx} size="small" label="Bairro" value={empresa.bairro ?? ''} onChange={e => atualizarEmpresa('bairro', e.target.value)} />
        <TextField sx={campoSx} size="small" label="CEP" value={empresa.cep ?? ''} onChange={e => atualizarEmpresa('cep', e.target.value)} />
        <TextField sx={campoSx} size="small" label="Município" value={empresa.municipio ?? ''} onChange={e => atualizarEmpresa('municipio', e.target.value)} />
        <TextField sx={campoSx} size="small" label="Código Município (IBGE)" type="number" value={empresa.codigoMunicipio ?? 0} onChange={e => atualizarEmpresa('codigoMunicipio', Number(e.target.value))} />
        <TextField sx={campoSx} size="small" label="UF" value={empresa.uf ?? ''} onChange={e => atualizarEmpresa('uf', e.target.value)} inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }} />
        <TextField sx={campoSx} size="small" label="Telefone" type="number" value={empresa.telefone ?? ''} onChange={e => atualizarEmpresa('telefone', e.target.value ? Number(e.target.value) : null)} />
        <TextField sx={campoSx} size="small" select label="Regime Tributário (CRT)" value={empresa.crt ?? 1} onChange={e => atualizarEmpresa('crt', Number(e.target.value))}>
          {CRT_OPCOES.map(o => (
            <MenuItem key={o.valor} value={o.valor}>{o.label}</MenuItem>
          ))}
        </TextField>
        <TextField sx={campoSx} size="small" label="Série NF" type="number" value={empresa.serie ?? 0} onChange={e => atualizarEmpresa('serie', Number(e.target.value))} />
      </Box>

      <Button variant="contained" sx={{ mt: 1 }} onClick={handleSalvarEmpresa}>Salvar Emitente</Button>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" component="h1" gutterBottom>
        Sugestão de Impostos por Ação
      </Typography>

      <TextField
        sx={{ ...campoSx, maxWidth: 360 }}
        size="small"
        select
        label="Ação Fiscal"
        value={acaoIndex}
        onChange={e => setAcaoIndex(Number(e.target.value))}
      >
        {acoes.map((a, i) => (
          <MenuItem key={a.id} value={i}>{TIPO_ACAO_FISCAL[a.tipoAcao] ?? a.tipoAcaoDescricao}</MenuItem>
        ))}
      </TextField>

      {acaoAtual && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            {ehAutorizar
              ? 'Estes valores valem para toda venda. Um produto só precisa ser editado quando exigir algo diferente — o valor do produto sobrescreve o que estiver aqui.'
              : 'Estes valores valem para esta operação. Os valores monetários vêm da nota de origem; aqui ficam apenas os códigos.'}
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>ICMS</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1 }}>
            <TextField sx={campoSx} size="small" label="Origem Mercadoria" type="number" value={acaoAtual.origemMercadoria ?? 0} onChange={e => atualizarAcao('origemMercadoria', Number(e.target.value))} />
            <TextField sx={campoSx} size="small" label="CSOSN (Simples)" value={acaoAtual.icmsCsosn ?? ''} onChange={e => atualizarAcao('icmsCsosn', e.target.value)} />
            <TextField sx={campoSx} size="small" label="CST (Reg. Normal)" value={acaoAtual.icmsCst ?? ''} onChange={e => atualizarAcao('icmsCst', e.target.value)} />
            <TextField sx={campoSx} size="small" label="Alíquota %" type="number" value={acaoAtual.icmsAliquota ?? ''} onChange={e => atualizarAcao('icmsAliquota', numeroOuNulo(e.target.value))} />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>PIS / COFINS</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1 }}>
            <TextField sx={campoSx} size="small" label="PIS CST" value={acaoAtual.pisCst ?? ''} onChange={e => atualizarAcao('pisCst', e.target.value)} />
            <TextField sx={campoSx} size="small" label="PIS Alíquota %" type="number" value={acaoAtual.pisAliquota ?? ''} onChange={e => atualizarAcao('pisAliquota', numeroOuNulo(e.target.value))} />
            <TextField sx={campoSx} size="small" label="COFINS CST" value={acaoAtual.cofinsCst ?? ''} onChange={e => atualizarAcao('cofinsCst', e.target.value)} />
            <TextField sx={campoSx} size="small" label="COFINS Alíquota %" type="number" value={acaoAtual.cofinsAliquota ?? ''} onChange={e => atualizarAcao('cofinsAliquota', numeroOuNulo(e.target.value))} />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>IPI</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
            <TextField sx={campoSx} size="small" label="IPI CST" value={acaoAtual.ipiCst ?? ''} onChange={e => atualizarAcao('ipiCst', e.target.value)} />
            <TextField sx={campoSx} size="small" label="IPI cEnq" value={acaoAtual.ipiCodigoEnquadramento ?? ''} onChange={e => atualizarAcao('ipiCodigoEnquadramento', e.target.value)} />
            <TextField sx={campoSx} size="small" label="IPI Alíquota %" type="number" value={acaoAtual.ipiAliquota ?? ''} onChange={e => atualizarAcao('ipiAliquota', numeroOuNulo(e.target.value))} />
          </Box>

          {/* IBS/CBS so existe na venda; devolucao e estorno nao emitem esses grupos. */}
          {ehAutorizar && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                IBS / CBS (reforma tributária)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Valores da fase de testes da SEFAZ. Só são emitidos quando a reforma tributária está ligada.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 1 }}>
                <TextField sx={campoSx} size="small" label="CST" placeholder="000" value={acaoAtual.ibsCbsCst ?? ''} onChange={e => atualizarAcao('ibsCbsCst', e.target.value)} />
                <TextField sx={campoSx} size="small" label="cClassTrib" placeholder="000001" value={acaoAtual.ibsCbsClassTrib ?? ''} onChange={e => atualizarAcao('ibsCbsClassTrib', e.target.value)} />
                <TextField sx={campoSx} size="small" label="IBS UF %" type="number" value={acaoAtual.ibsUfAliquota ?? ''} onChange={e => atualizarAcao('ibsUfAliquota', numeroOuNulo(e.target.value))} />
                <TextField sx={campoSx} size="small" label="IBS Municipal %" type="number" value={acaoAtual.ibsMunAliquota ?? ''} onChange={e => atualizarAcao('ibsMunAliquota', numeroOuNulo(e.target.value))} />
                <TextField sx={campoSx} size="small" label="CBS %" type="number" value={acaoAtual.cbsAliquota ?? ''} onChange={e => atualizarAcao('cbsAliquota', numeroOuNulo(e.target.value))} />
              </Box>
            </>
          )}

          {/* A venda ORIGINA o CFOP (escolha direta); devolução/estorno CONVERTEM o da nota original. */}
          {ehAutorizar ? (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>CFOP da venda</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField sx={campoSx} size="small" label="Dentro do estado" type="number" placeholder="5102" value={acaoAtual.cfopInterna ?? ''} onChange={e => atualizarAcao('cfopInterna', numeroOuNulo(e.target.value))} />
                <TextField sx={campoSx} size="small" label="Fora do estado" type="number" placeholder="6102" value={acaoAtual.cfopInterestadual ?? ''} onChange={e => atualizarAcao('cfopInterestadual', numeroOuNulo(e.target.value))} />
              </Box>
            </>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Conversões de CFOP (origem → destino)</Typography>
              {acaoAtual.conversoesCfop.map((cfop, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                  <TextField size="small" label="Origem" type="number" value={cfop.cfopOrigem} onChange={e => atualizarCfop(i, 'cfopOrigem', Number(e.target.value))} />
                  <TextField size="small" label="Destino" type="number" value={cfop.cfopDestino} onChange={e => atualizarCfop(i, 'cfopDestino', Number(e.target.value))} />
                  <Button color="error" onClick={() => removerCfop(i)}>Remover</Button>
                </Box>
              ))}
              <Button sx={{ mt: 0.5 }} onClick={adicionarCfop}>Adicionar CFOP</Button>
            </>
          )}

          <Box>
            <Button variant="contained" sx={{ mt: 1 }} onClick={handleSalvarAcao}>Salvar Ação</Button>
          </Box>
        </>
      )}
    </Box>
  )
}

export default ConfiguracaoFiscalTab
